import React, { useState, useEffect, useCallback } from 'react'
import {
  Row, Col, Button, Spinner, Input, FormGroup, Label,
  Modal, ModalHeader, ModalBody, ModalFooter, Card, CardBody,
} from 'reactstrap'
import ModeloEditor from './ModeloEditor'
import {
  getModelos, getBlocos, getSecoes, getClausulas, getContratoCampos,
  saveModelo, delModelo, handlerExiste, normalizaHandler,
} from './minutaApi'
import { notifyError, notifySuccess } from '../../utils/notify'

const ModelosTab = () => {
  const [loading, setLoading] = useState(true)
  const [modelos, setModelos] = useState([])
  const [blocos, setBlocos] = useState([])
  const [secoes, setSecoes] = useState([])
  const [clausulas, setClausulas] = useState([])
  const [campos, setCampos] = useState([])

  const [selecionado, setSelecionado] = useState(null) // modelo em edição
  const [modal, setModal] = useState({ open: false, record: null })
  const [form, setForm] = useState({ nome: '', hand: '', desc: '', handTouched: false })
  const [saving, setSaving] = useState(false)

  const carregarCatalogo = useCallback(async () => {
    const [b, s, c] = await Promise.all([getBlocos(), getSecoes(), getClausulas()])
    setBlocos(b || [])
    setSecoes(s || [])
    setClausulas(c || [])
  }, [])

  const carregar = useCallback(async () => {
    setLoading(true)
    const m = await getModelos()
    setModelos(m || [])
    setLoading(false)
  }, [])

  useEffect(() => {
    carregar()
    carregarCatalogo()
    getContratoCampos().then(setCampos)
  }, [carregar, carregarCatalogo])

  const abrirModal = (record = null) => {
    if (record) {
      setForm({
        nome: record.minutaMod_nome || '',
        hand: record.minutaMod_hand || '',
        desc: record.minutaMod_desc || '',
        handTouched: true,
      })
    } else {
      setForm({ nome: '', hand: '', desc: '', handTouched: false })
    }
    setModal({ open: true, record })
  }
  const fecharModal = () => setModal({ open: false, record: null })

  const salvarModelo = async () => {
    const nome = form.nome.trim()
    const hand = normalizaHandler(form.hand || form.nome)
    if (!nome) { notifyError('Informe o nome do modelo.'); return }
    if (!hand) { notifyError('Informe um handler válido.'); return }

    setSaving(true)
    try {
      const id = modal.record ? modal.record.minutaMod_id : null
      const dup = await handlerExiste('minutaMod', hand, id)
      if (dup) { notifyError(`O handler "${hand}" já está em uso.`); setSaving(false); return }

      const data = { minutaMod_nome: nome, minutaMod_hand: hand, minutaMod_desc: form.desc || null }
      if (id) data.minutaMod_id = id
      const savedId = await saveModelo(data)
      notifySuccess(modal.record ? 'Modelo atualizado.' : 'Modelo criado.')
      setSaving(false)
      fecharModal()
      await carregar()

      // abre o editor logo após criar
      if (!modal.record) {
        setSelecionado({ minutaMod_id: savedId, minutaMod_nome: nome, minutaMod_hand: hand, minutaMod_desc: form.desc })
      }
    } catch (err) {
      console.log('Erro ao salvar modelo', err)
      notifyError('Erro ao salvar modelo.')
      setSaving(false)
    }
  }

  const excluirModelo = async (m) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Excluir o modelo "${m.minutaMod_nome}"?`)) return
    await delModelo(m.minutaMod_id)
    notifySuccess('Modelo excluído.')
    carregar()
  }

  if (selecionado) {
    return (
      <ModeloEditor
        model={selecionado}
        blocos={blocos}
        secoes={secoes}
        clausulas={clausulas}
        campos={campos}
        onBack={() => { setSelecionado(null); carregar() }}
      />
    )
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Modelos de Minuta</h5>
        <Button color="primary" size="sm" onClick={() => abrirModal()}>
          <i className="mdi mdi-plus mr-1" /> Novo Modelo
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-5"><Spinner /></div>
      ) : (
        <Row>
          {modelos.length === 0 && (
            <Col xs={12}><div className="text-muted text-center py-4">Nenhum modelo cadastrado.</div></Col>
          )}
          {modelos.map((m) => (
            <Col md={4} key={m.minutaMod_id} className="mb-3">
              <Card className="h-100">
                <CardBody>
                  <div className="d-flex justify-content-between align-items-start">
                    <h6 className="mb-1">{m.minutaMod_nome}</h6>
                    <span className="minuta-sec-hand">{m.minutaMod_hand}</span>
                  </div>
                  <p className="text-muted small mb-3" style={{ minHeight: 36 }}>
                    {m.minutaMod_desc || 'Sem descrição.'}
                  </p>
                  <div className="d-flex">
                    <Button color="primary" size="sm" className="mr-2" onClick={() => setSelecionado(m)}>
                      <i className="mdi mdi-pencil-ruler mr-1" /> Montar
                    </Button>
                    <Button color="light" size="sm" className="mr-2" onClick={() => abrirModal(m)}>
                      Editar
                    </Button>
                    <Button color="link" size="sm" className="text-danger" onClick={() => excluirModelo(m)}>
                      <i className="mdi mdi-delete" />
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Modal isOpen={modal.open} toggle={fecharModal} backdrop="static">
        <ModalHeader toggle={fecharModal}>{modal.record ? 'Editar modelo' : 'Novo modelo'}</ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Nome</Label>
            <Input
              value={form.nome}
              onChange={(e) => setForm((f) => ({
                ...f,
                nome: e.target.value,
                hand: f.handTouched ? f.hand : normalizaHandler(e.target.value),
              }))}
            />
          </FormGroup>
          <FormGroup>
            <Label>Handler (apelido único)</Label>
            <Input
              value={form.hand}
              onChange={(e) => setForm((f) => ({ ...f, hand: e.target.value, handTouched: true }))}
              onBlur={(e) => setForm((f) => ({ ...f, hand: normalizaHandler(e.target.value) }))}
            />
          </FormGroup>
          <FormGroup>
            <Label>Descrição</Label>
            <Input type="textarea" rows={3} value={form.desc} onChange={(e) => setForm((f) => ({ ...f, desc: e.target.value }))} />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={fecharModal} disabled={saving}>Cancelar</Button>
          <Button color="primary" onClick={salvarModelo} disabled={saving}>
            {saving ? <Spinner size="sm" /> : 'Salvar'}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default ModelosTab
